import * as focusTrap from 'focus-trap';
import { repaint } from '../../../js/utils/node';
import { hide as hideOverlay, show as showOverlay } from '../../../js/utils/overlay';

// Nested drawer stacking — keep in sync with drawer.css:
// .drawer (z-50), .drawer--active (z-60), .openable--nested (z-70)
const NESTED_OVERLAY_Z_INDEX = 65;

class Openable {
  static stack = [];

  constructor(element, useOverlay = true) {
    this.isActive = false;
    this.useOverlay = useOverlay;
    // Subclasses may set this (e.g. Drawer: 'transform', Modal: 'opacity').
    // When null, the first transitionend on the container completes the close.
    this.closeTransitionProperty = null;

    if (typeof element === 'string' || element instanceof String) {
      this.container = document.querySelector(element);
    } else {
      this.container = element;
    }

    if (!this.container) {
      console.warn(`Openable: element not found — "${element}"`);
      return;
    }

    this.container.hidden = true;
    this.setState(false);

    this.focusTrap = focusTrap.createFocusTrap(this.container, {
      allowOutsideClick: true,
    });

    this.clickEvent = this.clickEvent.bind(this);
    this.keydownEvent = this.keydownEvent.bind(this);
    this.addEventListeners = this.addEventListeners.bind(this);
    this.on = this.on.bind(this);
  }

  addEventListeners() {
    document.addEventListener('touchstart', this.clickEvent);
    document.addEventListener('click', this.clickEvent);
    document.addEventListener('keydown', this.keydownEvent);
    this.focusTrap.activate();
  }

  removeEventListeners({ returnFocus = true } = {}) {
    document.removeEventListener('touchstart', this.clickEvent);
    document.removeEventListener('click', this.clickEvent);
    document.removeEventListener('keydown', this.keydownEvent);
    this.focusTrap.deactivate({ returnFocusOnDeactivate: returnFocus });
  }

  suspend() {
    this.removeEventListeners({ returnFocus: false });
  }

  resume() {
    this.addEventListeners();
  }

  clickEvent(event) {
    // Use composedPath so clicks still count as "inside" when a handler
    // removes event.target from the DOM before this listener runs (e.g. filter chips).
    const clickedInside = event.composedPath().includes(this.container);

    if (!clickedInside) {
      event.stopPropagation();

      this.close();
    }
  }

  keydownEvent(event) {
    if (event.key === 'Escape') {
      this.close();
    }
  }

  setState(state) {
    if (!this.container) return;
    this.isActive = state;
    this.container.setAttribute('aria-hidden', state ? 'false' : 'true');
  }

  toggle() {
    if (!this.container) return;
    if (this.isActive) {
      this.close();
    } else {
      this.open();
    }
  }

  open() {
    if (!this.container || this.isActive) return;

    const isNested = Openable.stack.length > 0;

    if (isNested) {
      Openable.stack[Openable.stack.length - 1].suspend();
      this.container.classList.add('openable--nested');
    } else {
      document.documentElement.style.overflow = 'hidden';
    }

    Openable.stack.push(this);

    this.setState(true);
    this.container.hidden = false;
    repaint(this.container);

    this.container.classList.add(this.classes.active);

    if (this.useOverlay) {
      // Overlay at NESTED_OVERLAY_Z_INDEX sits between parent drawer (z-60) and nested drawer (z-70).
      showOverlay(isNested ? NESTED_OVERLAY_Z_INDEX : null);
    }

    setTimeout(() => {
      this.addEventListeners();
    }, 0);

    if (this.onOpen) {
      this.onOpen();
    }
  }

  close() {
    if (!this.container || !this.isActive) return;

    const stackIndex = Openable.stack.indexOf(this);

    if (stackIndex === -1 || stackIndex !== Openable.stack.length - 1) {
      return;
    }

    Openable.stack.pop();

    this.setState(false);
    this.container.classList.remove(this.classes.active);
    this.removeEventListeners();

    const hasOpenParent = Openable.stack.length > 0;

    let closeFinished = false;

    const finishClose = () => {
      if (closeFinished || this.isActive) return;

      closeFinished = true;
      this.container.removeEventListener('transitionend', onTransitionEnd);
      clearTimeout(closeFallbackTimeout);

      this.container.classList.remove('openable--nested');
      this.container.hidden = true;

      if (this.onClosed) {
        this.onClosed();
      }

      if (hasOpenParent) {
        Openable.stack[Openable.stack.length - 1].resume();

        if (this.useOverlay) {
          showOverlay();
        }
      }
    };

    const onTransitionEnd = (event) => {
      if (event.target !== this.container) {
        return;
      }

      if (this.closeTransitionProperty && event.propertyName !== this.closeTransitionProperty) {
        return;
      }

      finishClose();
    };

    const transitionDuration = parseFloat(getComputedStyle(this.container).transitionDuration);
    const hasDuration = transitionDuration !== 0;
    let closeFallbackTimeout;

    if (hasDuration) {
      this.container.addEventListener('transitionend', onTransitionEnd);
      closeFallbackTimeout = setTimeout(finishClose, transitionDuration * 1000 + 50);
    } else {
      finishClose();
    }

    if (this.onClose) {
      this.onClose();
    }

    if (!hasOpenParent) {
      document.documentElement.style.overflow = '';

      if (this.useOverlay) {
        hideOverlay();
      }
    }
  }

  on(event, func) {
    if (event === 'close') {
      this.onClose = func;
    }

    if (event === 'closed') {
      this.onClosed = func;
    }

    if (event === 'open') {
      this.onOpen = func;
    }
  }
}

export default Openable;
